import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, GitFork, NotebookPen } from "lucide-react";
import { toast } from "sonner";
import { usePostHog } from "@posthog/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useGraphData } from "@/hooks/useGraphData";
import { useExploreState } from "@/hooks/useExploreState";
import { computeVisibleIds } from "@/lib/graphLayout";
import { ExploreGraph } from "@/components/explore/ExploreGraph";
import { GraphToolbar } from "@/components/explore/GraphToolbar";
import { Breadcrumb } from "@/components/explore/Breadcrumb";
import { GraphSidebar } from "@/components/explore/GraphSidebar";
import { DetailPanel } from "@/components/explore/DetailPanel";
import { TimelineView } from "@/components/explore/TimelineView";
import { LensToggle } from "@/components/explore/LensToggle";

// The graph + timeline lenses of /explore (?view=map / ?view=timeline).
export function MapView() {
  const { user } = useAuth();
  const posthog = usePostHog();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusParam = searchParams.get("node") || searchParams.get("focus");
  const isTimeline = searchParams.get("view") === "timeline";

  const setView = useCallback(
    (v) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (v === "timeline") next.set("view", "timeline");
          else next.set("view", "map");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const { nodes, edges, nodeMap, loading, error } = useGraphData();
  const state = useExploreState();
  const [schema, setSchema] = useState(null);
  const [rfInstance, setRfInstance] = useState(null);
  const [learnedIds, setLearnedIds] = useState([]);
  const [noteCounts, setNoteCounts] = useState({});
  const [myLayer, setMyLayer] = useState(false);
  const [overview, setOverview] = useState(!focusParam);

  const allIds = useMemo(() => new Set(nodes.map((n) => n.id)), [nodes]);

  const drillIn = useCallback((id) => { setOverview(false); state.focus(id); }, [state]);

  useEffect(() => {
    api.get("/kg/schema").then((res) => setSchema(res.data)).catch(() => {});
    document.title = "Explore the Graph — Living Wisdom Explorer";
  }, []);

  useEffect(() => {
    if (!user) {
      setLearnedIds([]);
      return;
    }
    api.get("/progress").then((res) => setLearnedIds(res.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) {
      setNoteCounts({});
      setMyLayer(false);
      return;
    }
    api.get("/notes").then((res) => {
      const m = {};
      res.data.forEach((n) => {
        const k = n.node_id || n.track_id;
        if (k) m[k] = (m[k] || 0) + 1;
      });
      setNoteCounts(m);
    }).catch(() => {});
  }, [user, state.panelOpen]);

  useEffect(() => {
    if (state.rootId || nodes.length === 0) return;
    const preferred =
      (focusParam && nodeMap[focusParam] && focusParam) ||
      nodes.find((n) => n.type === "knowledge_system")?.id ||
      nodes[0].id;
    state.init(preferred);
    if (focusParam && nodeMap[focusParam]) {
      setOverview(false);
      setTimeout(() => state.focus(focusParam), 0);
    }
  }, [nodes, nodeMap, focusParam, state]);

  const visibleIds = useMemo(
    () => computeVisibleIds(edges, state.rootId, state.expandedIds),
    [edges, state.rootId, state.expandedIds]
  );
  const effectiveVisibleIds = overview ? allIds : visibleIds;
  const visKey = useMemo(() => Array.from(effectiveVisibleIds).sort().join(","), [effectiveVisibleIds]);

  const lastViewedRef = useRef(null);
  useEffect(() => {
    const id = state.selectedId;
    if (state.panelOpen && id && nodeMap[id] && lastViewedRef.current !== id) {
      lastViewedRef.current = id;
      const n = nodeMap[id];
      posthog?.capture("entity_viewed", { entity_id: n.id, entity_type: n.type, entity_label: n.label });
    }
    if (!state.panelOpen) lastViewedRef.current = null;
  }, [state.selectedId, state.panelOpen, nodeMap, posthog]);

  useEffect(() => {
    if (!rfInstance) return;
    const t = setTimeout(() => rfInstance.fitView({ duration: 400, padding: 0.25 }), 80);
    return () => clearTimeout(t);
  }, [visKey, rfInstance]);

  const toggleLearned = useCallback(async (id) => {
    try {
      const res = await api.post("/progress/toggle", { node_id: id });
      setLearnedIds((prev) => (res.data.learned ? [...prev, id] : prev.filter((x) => x !== id)));
      toast.success(res.data.learned ? "Marked as learned" : "Marked as not learned");
    } catch {
      toast.error("Please sign in to track progress.");
    }
  }, []);

  const handleJumpTo = useCallback(
    (id) => {
      if (isTimeline) {
        setView("map");
        state.focus(id);
      } else {
        state.jumpTo(id);
      }
    },
    [isTimeline, setView, state]
  );

  return (
    <div className="relative flex-1 min-h-0 flex flex-col" data-testid="explore-map-view">
      <div className="flex items-center gap-3 px-4 py-2 border-b border-[#14140f]/8 overflow-x-auto">
        <LensToggle nodeId={state.selectedId || state.rootId} />
        {user && !isTimeline && (
          <button
            data-testid="my-layer-toggle"
            onClick={() => setMyLayer((v) => !v)}
            title="Pin your notes onto the map"
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors shrink-0 ${
              myLayer
                ? "bg-[#ffb020] text-[#14140f] border-[#ffb020]"
                : "bg-[#f5f4f0] text-[#4e6073] border-[#14140f]/10 hover:text-[#14140f]"
            }`}
          >
            <NotebookPen className="w-3.5 h-3.5" />
            My layer
          </button>
        )}
        {!isTimeline && (
          <button
            data-testid="full-map-toggle"
            onClick={() => { setOverview((v) => !v); if (!overview) state.closePanel(); }}
            title="See the whole knowledge graph"
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors shrink-0 ${
              overview
                ? "bg-[#ff7360] text-[#14140f] border-[#ff7360]"
                : "bg-[#f5f4f0] text-[#4e6073] border-[#14140f]/10 hover:text-[#14140f]"
            }`}
          >
            <GitFork className="w-3.5 h-3.5" />
            Full map
          </button>
        )}
        {!isTimeline && (
          <div className="flex-1 min-w-0 hidden sm:block">
            <Breadcrumb breadcrumb={state.breadcrumb} nodeMap={nodeMap} onJump={state.goToBreadcrumb} />
          </div>
        )}
      </div>

      <div className="relative flex-1 overflow-hidden">
        {isTimeline ? (
          <TimelineView onSelect={state.select} activeId={state.selectedId} />
        ) : (
          <>
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center text-[#14140f]/40">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            )}
            {error && (
              <div className="absolute inset-0 flex items-center justify-center text-[#b34040] text-sm">
                {error}
              </div>
            )}
            {!loading && !error && (
              <div className="absolute inset-0 flex">
                <div className="relative flex-1 min-w-0">
                  <ExploreGraph
                    state={state}
                    visibleIds={effectiveVisibleIds}
                    nodeMap={nodeMap}
                    edges={edges}
                    onInit={setRfInstance}
                    myLayer={myLayer}
                    noteCounts={noteCounts}
                    onDrillIn={drillIn}
                    overview={overview}
                  />
                  <GraphToolbar rfInstance={rfInstance} state={state} />
                </div>
                <GraphSidebar
                  nodes={nodes}
                  onSelect={drillIn}
                  activeId={state.selectedId || state.rootId}
                />
              </div>
            )}
          </>
        )}
        <DetailPanel
          selectedId={state.selectedId}
          panelOpen={state.panelOpen}
          schema={schema}
          learnedIds={learnedIds}
          onToggleLearned={toggleLearned}
          onClose={state.closePanel}
          onJumpTo={handleJumpTo}
        />
      </div>
    </div>
  );
}
